import { matchPath } from 'react-router';
import type { GlossaryKey } from '@/lib/glossary';

/** What a screen is for and how to use it, in words a twelve-year-old understands. */
export interface HelpTopic {
  /** Route pattern, as in `app/router.tsx`. More specific patterns go first. */
  pattern: string;
  title: string;
  what: string;
  canDo: string[];
  steps?: string[];
  words?: GlossaryKey[];
}

export const HELP: HelpTopic[] = [
  {
    pattern: '/inicio',
    title: 'Inicio',
    what: 'Es tu escritorio. Te muestra lo que tenés que hacer hoy, ordenado por lo más urgente, y cómo viene el negocio.',
    canDo: [
      'Ver la lista "Tu próxima acción": cada fila es algo que espera por vos. Tocala y te lleva justo ahí.',
      'Mirar los números de arriba: presupuestos sin terminar, enviados, aceptados y trabajos en marcha.',
      'Empezar un presupuesto nuevo con el botón dorado.',
    ],
    steps: [
      'Leé la primera fila de "Tu próxima acción": es lo más urgente.',
      'Tocala y hacé lo que te pide la pantalla (siempre hay un botón dorado con el siguiente paso).',
      'Volvé a Inicio y seguí con la próxima.',
    ],
    words: ['presupuesto', 'trabajo', 'sena', 'saldo'],
  },
  {
    pattern: '/consultas/:id',
    title: 'Una consulta',
    what: 'Acá está todo lo que sabemos de lo que pidió esta persona: la charla, los datos y el camino hasta el presupuesto.',
    canDo: [
      'Leer la charla de WhatsApp y los datos que salieron de ella.',
      'Confirmar o corregir los datos (medidas, material, dirección).',
      'Seguir la tarjeta "Lo que sigue": elegir el cliente, la obra y armar el presupuesto ya cargado.',
    ],
    steps: [
      'Revisá los datos de la derecha y confirmá los que estén bien.',
      'Tocá el botón dorado: te pide el cliente y la obra (si no existen, los creás ahí mismo).',
      'Cuando está todo, el botón dorado te lleva al presupuesto con los datos ya puestos.',
    ],
    words: ['consulta', 'cliente', 'obra', 'presupuesto'],
  },
  {
    pattern: '/consultas',
    title: 'Consultas',
    what: 'Cada vez que alguien pregunta por un trabajo (por WhatsApp, teléfono o en persona) se anota acá. Es el primer paso antes del presupuesto.',
    canDo: [
      'Anotar una consulta nueva con el botón "Nueva consulta".',
      'Ver en qué está cada una: nueva, juntando datos, lista para presupuestar o ya presupuestada.',
      'Abrir una consulta para seguir con ella.',
    ],
    steps: [
      'Tocá "Nueva consulta" y escribí quién pregunta y qué necesita.',
      'Abrila y completá lo que falte (medidas, material, dirección).',
      'Seguí el botón dorado hasta llegar al presupuesto.',
    ],
    words: ['consulta', 'presupuesto'],
  },
  {
    pattern: '/presupuestos/nuevo',
    title: 'Armar un presupuesto',
    what: 'Acá armás el precio de un trabajo para mandarle al cliente. Vas en 5 pasos y el total se calcula solo mientras cargás.',
    canDo: [
      'Seguir los pasos de arriba: te marcan cuáles están listos y cuál sigue.',
      'Pegar piezas desde una planilla de Excel.',
      'Ver cómo le va a llegar al cliente con "Vista previa".',
    ],
    steps: [
      'Paso 1: elegí el cliente y la obra (dónde va el trabajo) y a nombre de quién sale.',
      'Paso 2: cargá cada pieza con su largo, ancho y material. Ej.: mesada 200 × 60 cm.',
      'Paso 3: sumá los adicionales: colocación, viaje, bachas, agujeros.',
      'Paso 4: revisá las condiciones: hasta cuándo vale y cuánto es la seña.',
      'Paso 5: tocá "Terminar y generar el PDF" y mandáselo al cliente por WhatsApp.',
    ],
    words: ['cliente', 'obra', 'titular', 'pieza', 'adicionales', 'condiciones', 'sena', 'enviar'],
  },
  {
    pattern: '/presupuestos/:id',
    title: 'Un presupuesto',
    what: 'Es el precio de un trabajo para un cliente. Los pasos de arriba te dicen qué falta; el botón dorado, qué hacer ahora.',
    canDo: [
      'Mientras es borrador: cambiar lo que quieras, se guarda solo.',
      'Terminarlo y generar el PDF para el cliente, y compartirlo por WhatsApp.',
      'Cuando el cliente dice que sí, tocar "El cliente dijo que sí": se crea el trabajo.',
      'Si hay que cambiar algo después de enviarlo, hacer una versión nueva (la anterior queda guardada).',
      'Hacer un descuento en "Condiciones": por porcentaje o por monto, con el motivo que lee el cliente.',
      'Ver cada pieza dibujada según sus medidas (la mesada acostada, el zócalo parado) y el ambiente entero en 3D con "Ver en 3D".',
    ],
    steps: [
      'Fijate en los pasos de arriba: los que tienen tilde ya están.',
      'Completá el que está marcado como "ahora".',
      'Tocá el botón dorado de la derecha (o el de abajo en el celular) para pasar a lo siguiente.',
    ],
    words: ['borrador', 'enviar', 'version', 'vigencia', 'sena', 'aceptado', 'ganancia'],
  },
  {
    pattern: '/presupuestos',
    title: 'Presupuestos',
    what: 'Todos los presupuestos, desde que se empiezan a armar hasta que el cliente los acepta.',
    canDo: [
      'Armar uno nuevo con "Nuevo presupuesto".',
      'Filtrar por borradores, enviados, aceptados o anulados.',
      'Abrir uno para seguir armándolo o ver cómo quedó.',
      'Sacar los de prueba con "Limpiar lista": los que nunca se enviaron se eliminan; los que ya tenían PDF se anulan y quedan en "Anulados".',
    ],
    words: ['presupuesto', 'borrador', 'enviar', 'aceptado'],
  },
  {
    pattern: '/clientes/:id',
    title: 'Un cliente',
    what: 'Los datos del cliente, sus obras y todo lo que le presupuestamos.',
    canDo: ['Editar sus datos.', 'Agregar una obra (la dirección donde va el trabajo).', 'Empezar un presupuesto para este cliente.'],
    words: ['cliente', 'obra'],
  },
  {
    pattern: '/clientes',
    title: 'Clientes y obras',
    what: 'Las personas o empresas que nos compran. Cada cliente puede tener varias obras (por ejemplo, su casa y su local).',
    canDo: ['Buscar un cliente por nombre o teléfono.', 'Crear un cliente nuevo con "Nuevo cliente".', 'Abrir un cliente para ver sus obras y presupuestos.'],
    steps: ['Tocá "Nuevo cliente" y poné nombre y teléfono.', 'Agregale la obra con su dirección.', 'Desde ahí, armá el presupuesto.'],
    words: ['cliente', 'obra'],
  },
  {
    pattern: '/trabajos/:id',
    title: 'Un trabajo',
    what: 'Un presupuesto que el cliente aceptó. Acá se sigue todo hasta el final: medir, fabricar, entregar y cobrar.',
    canDo: [
      'Seguir la tarjeta dorada "Lo que sigue": siempre te dice el próximo paso y tiene el botón para hacerlo.',
      'Ver en qué etapa está (la fila de circulitos).',
      'Cobrar: en la pestaña "Cobros" anotás cada pago del cliente.',
      'Entregar: en "Entrega" marcás qué piezas se llevaron.',
      'Cargar los gastos en la pestaña "Gastos" para saber cuánto ganamos.',
    ],
    steps: [
      'Leé la frase de la tarjeta dorada: te dice qué falta.',
      'Tocá su botón (por ejemplo "Cobrar la seña") y completá lo que te pide.',
      'Cuando termines cada etapa del taller, el botón te deja pasar a la siguiente.',
      'Al final: entregar, cobrar lo que falta, cargar los gastos y cerrar.',
    ],
    words: ['trabajo', 'etapa', 'sena', 'saldo', 'cobro', 'pagoAvisado', 'entrega', 'costos', 'ganancia', 'deshacer'],
  },
  {
    pattern: '/trabajos',
    title: 'Trabajos',
    what: 'Cada presupuesto aceptado se convierte en un trabajo. Acá están todos, con su etapa y lo que falta cobrar.',
    canDo: ['Filtrar por etapa (medición, corte, listo, entregado…).', 'Abrir un trabajo para ver qué le falta y seguir con él.'],
    words: ['trabajo', 'etapa', 'saldo'],
  },
  {
    pattern: '/agenda',
    title: 'Agenda y rutas',
    what: 'Las visitas de la semana: ir a medir, a entregar o a colocar. Y el mejor camino para hacerlas en un día.',
    canDo: ['Ver qué hay que hacer cada día.', 'Programar una visita nueva.', 'Ordenar la ruta del día con el botón dorado y abrirla en Google Maps.'],
    words: ['visita', 'obra'],
  },
  {
    pattern: '/mapa',
    title: 'Mapa de obras',
    what: 'Los trabajos en marcha puestos en el mapa, cada uno con el color de su etapa. El taller está en dorado.',
    canDo: ['Tocar una obra para ver qué le falta.', 'Programar la próxima visita desde ahí.'],
    words: ['obra', 'etapa', 'visita'],
  },
  {
    pattern: '/stock/:id',
    title: 'Una placa o sobrante',
    what: 'Todo sobre esta pieza de piedra: medidas, de dónde vino, si está apartada y para qué trabajo se usó.',
    canDo: [
      'Reservarla para un trabajo, así nadie más la usa.',
      'Registrar el corte: qué se llevó el trabajo, qué sobró y qué se perdió.',
      'Pasarle su costo a un trabajo.',
    ],
    steps: [
      'Tocá el botón dorado: te muestra lo que sigue para esta placa.',
      'Al cortarla, anotá los sobrantes con su medida: quedan en el stock para usar después.',
    ],
    words: ['placa', 'sobrante', 'reservar', 'merma', 'costos'],
  },
  {
    pattern: '/stock',
    title: 'Stock de placas',
    what: 'Las placas enteras y los sobrantes que hay en el taller. Sirve para no comprar de más y aprovechar lo que sobra.',
    canDo: [
      'Cargar una placa nueva con "Cargar placa".',
      'Buscar una pieza que sirva para un trabajo, por material y medida.',
      'Abrir una placa para reservarla o registrar el corte.',
    ],
    steps: [
      'Cuando llega una placa del proveedor, tocá "Cargar placa" y poné material y medidas.',
      'Cuando un trabajo necesita piedra, buscá por medida: primero mirá los sobrantes.',
      'Abrí la que sirva y reservala para ese trabajo.',
    ],
    words: ['placa', 'sobrante', 'reservar', 'merma'],
  },
  {
    pattern: '/productos/:id',
    title: 'Un producto',
    what: 'Un producto chico que hacemos (por ejemplo, una tabla de picar): sus tandas de fabricación, cuántos hay y cuánto ganamos.',
    canDo: ['Empezar una tanda nueva.', 'Ver cuántos quedan y cuánto se vendió.'],
    words: ['sobrante', 'ganancia'],
  },
  {
    pattern: '/productos',
    title: 'Productos',
    what: 'Cosas chicas que fabricamos con sobrantes para vender por unidad: tablas, estantes, posavasos.',
    canDo: ['Crear un producto.', 'Ver cuántos hay y cuánto deja cada uno.'],
    words: ['sobrante', 'ganancia'],
  },
  {
    pattern: '/proveedores/:id',
    title: 'Un proveedor',
    what: 'Sus datos, lo que le compramos, lo que le debemos y los precios que nos pasó.',
    canDo: ['Cargar una compra.', 'Pagarle lo que le debemos.', 'Anotar un precio nuevo que nos pasó.'],
    words: ['proveedor', 'deuda'],
  },
  {
    pattern: '/proveedores',
    title: 'Proveedores',
    what: 'Las empresas que nos venden piedra, bachas y otros materiales.',
    canDo: ['Agregar un proveedor.', 'Ver a quién le debemos plata.', 'Abrir uno para pagarle o ver sus precios.'],
    words: ['proveedor', 'deuda'],
  },
  {
    pattern: '/comprobantes/:id',
    title: 'Revisar una factura',
    what: 'A la izquierda está el archivo que mandó el proveedor y a la derecha los datos. Revisás que coincidan y confirmás.',
    canDo: ['Completar o corregir los datos mirando el archivo.', 'Elegir a qué trabajo va el gasto, si es para uno.', 'Confirmarla, o marcarla como "no sirve".'],
    steps: [
      'Mirá el archivo y fijate el proveedor, la fecha y el total.',
      'Completá los datos de la derecha (los que tienen * son obligatorios).',
      'Si la compra es para un trabajo, elegilo.',
      'Tocá "Confirmar factura": queda anotado lo que le debemos y lo que costó.',
    ],
    words: ['comprobante', 'proveedor', 'deuda', 'razonSocial', 'cuit', 'iva'],
  },
  {
    pattern: '/comprobantes',
    title: 'Facturas de proveedores',
    what: 'Acá se suben las facturas, remitos y presupuestos que nos mandan los proveedores, para anotar lo que compramos.',
    canDo: ['Subir un archivo (foto o PDF) arrastrándolo al recuadro.', 'Ver cuáles faltan revisar.', 'Abrir una para revisarla y confirmarla.'],
    steps: ['Arrastrá la foto o el PDF de la factura al recuadro (o tocalo para elegirla).', 'Abrila y revisá los datos.', 'Confirmala.'],
    words: ['comprobante', 'proveedor', 'deuda'],
  },
  {
    pattern: '/precios',
    title: 'Precios y alertas',
    what: 'Lo que nos cobra cada proveedor. Si un precio sube, aparece una alerta con los presupuestos que se ven afectados.',
    canDo: ['Anotar un precio nuevo.', 'Revisar las alertas de aumento.'],
    words: ['proveedor'],
  },
  {
    pattern: '/caja',
    title: 'Caja',
    what: 'Toda la plata que entró y salió de nuestras cuentas: lo que pagan los clientes, lo que devolvemos y lo que les pagamos a los proveedores.',
    canDo: [
      'Ver cuánto hay en cada cuenta, arriba.',
      'Elegir el período: hoy, ayer, 7 días, este mes.',
      'Tocar un movimiento para ir al trabajo del que viene.',
    ],
    steps: ['Elegí el período que querés ver.', 'Mirá los totales: lo que entró, lo que salió y lo que queda.', 'Si algo no te cierra, tocá el movimiento para ver de dónde salió.'],
    words: ['caja', 'cuenta', 'cobro', 'deshacer'],
  },
  {
    pattern: '/cuentas',
    title: 'Nuestras cuentas',
    what: 'Dónde guardamos la plata: bancos, Mercado Pago y la caja de efectivo del taller.',
    canDo: ['Agregar una cuenta.', 'Cambiarle el nombre o darla de baja.'],
    words: ['cuenta', 'titular'],
  },
  {
    pattern: '/tableros',
    title: 'Gráficos y números',
    what: 'Cómo viene el negocio, en gráficos: la plata, los trabajos, los productos y la publicidad.',
    canDo: ['Mirar cada gráfico.', 'Ver la tabla de abajo con los números exactos.'],
    words: ['ganancia', 'caja'],
  },
  {
    pattern: '/catalogo',
    title: 'Lista de precios',
    what: 'Todo lo que vendemos con su precio. El presupuesto saca los precios de acá.',
    canDo: ['Cambiar un precio (te pide el motivo, para que quede anotado).', 'Agregar un material o servicio nuevo.'],
    words: ['catalogo', 'presupuesto'],
  },
  {
    pattern: '/cotizacion',
    title: 'Precio del dólar',
    what: 'El dólar que usamos para pasar precios de dólares a pesos. Se actualiza solo.',
    canDo: ['Ver el precio de hoy y de los días anteriores.', 'Si la página de Ámbito no anda, cargarlo a mano (con permiso).'],
    words: ['dolar'],
  },
  {
    pattern: '/titulares',
    title: 'A nombre de quién',
    what: 'Las personas o empresas nuestras que firman los presupuestos y cobran. Se elige una en cada presupuesto.',
    canDo: ['Agregar o editar uno.'],
    words: ['titular'],
  },
  {
    pattern: '/mercado-libre/*',
    title: 'Mercado Libre',
    what: 'Lo que publicamos y vendemos por Mercado Libre. Cada publicación la aprueba alguien antes de salir.',
    canDo: ['Crear una publicación.', 'Ver las ventas.'],
  },
  {
    pattern: '/marketing/*',
    title: 'Marketing',
    what: 'Las publicidades que hacemos y cuántos clientes nos trajo cada una.',
    canDo: ['Crear una campaña.', 'Anotar cuánto se gastó y cuántas consultas trajo.'],
  },
  {
    pattern: '/sistema/usuarios',
    title: 'Usuarios',
    what: 'Las personas que pueden entrar al sistema.',
    canDo: ['Agregar a alguien.', 'Elegir qué rol tiene (eso define qué puede hacer).', 'Cambiarle la contraseña.'],
  },
  {
    pattern: '/sistema/roles',
    title: 'Roles y permisos',
    what: 'Un rol es un grupo de cosas que una persona puede hacer. Por ejemplo, "Ventas" puede armar presupuestos pero no ver la caja.',
    canDo: ['Crear o cambiar un rol.', 'Tildar qué puede hacer.'],
  },
  {
    pattern: '/configuracion',
    title: 'Configuración',
    what: 'Los datos del negocio y la conexión con otros servicios (Google Maps, Mercado Libre, WhatsApp).',
    canDo: ['Cambiar los datos del negocio.', 'Conectar un servicio y probar que ande.'],
  },
  {
    pattern: '/sistema/auditoria',
    title: 'Historial de cambios',
    what: 'Cada cambio que se hace en el sistema queda anotado: quién lo hizo y cuándo. No se puede borrar.',
    canDo: ['Buscar qué pasó con algo y quién lo cambió.'],
  },
  {
    pattern: '/sistema/tareas',
    title: 'Tareas automáticas',
    what: 'Cosas que el sistema hace solo, como calcular distancias. Si alguna falló, se puede probar de nuevo.',
    canDo: ['Ver si algo falló.', 'Reintentarlo.'],
  },
];

export function helpFor(pathname: string): HelpTopic | null {
  return HELP.find((h) => matchPath({ path: h.pattern, end: true }, pathname)) ?? null;
}
